import { Injectable, signal } from '@angular/core';

export type SnackbarVariant = 'default' | 'success' | 'error' | 'warning';

export interface AppSnackbarOptions {
  title?: string;
  variant?: SnackbarVariant;
  duration?: number;
}

export interface SnackbarState {
  variant: SnackbarVariant;
  message: string;
  title?: string;
  duration: number;
}

@Injectable({ providedIn: 'root' })
export class AppSnackbarService {
  private static readonly DEFAULT_DURATIONS: Record<SnackbarVariant, number> = {
    default: 3000,
    success: 3000,
    error: 5000,
    warning: 5000
  };

  private readonly _state = signal<SnackbarState | null>(null);
  readonly state = this._state.asReadonly();

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private startedAt = 0;
  private remaining = 0;

  show(message: string, options: AppSnackbarOptions = {}): void {
    const variant = options.variant ?? 'default';
    const duration = options.duration ?? AppSnackbarService.DEFAULT_DURATIONS[variant];

    this._state.set({ message, title: options.title, variant, duration });
    this.startTimer(duration);
  }

  success(message: string, options: Omit<AppSnackbarOptions, 'variant'> = {}): void {
    this.show(message, { ...options, variant: 'success' });
  }

  error(message: string, options: Omit<AppSnackbarOptions, 'variant'> = {}): void {
    this.show(message, { ...options, variant: 'error' });
  }

  warning(message: string, options: Omit<AppSnackbarOptions, 'variant'> = {}): void {
    this.show(message, { ...options, variant: 'warning' });
  }

  pause(): void {
    if (this.timerId === null) return;
    clearTimeout(this.timerId);
    this.timerId = null;
    this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
  }

  resume(): void {
    if (this.timerId !== null || this._state() === null) return;
    if (this.remaining <= 0) {
      this.dismiss();
      return;
    }
    this.startedAt = Date.now();
    this.timerId = setTimeout(() => this.dismiss(), this.remaining);
  }

  dismiss(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.remaining = 0;
    this.startedAt = 0;
    this._state.set(null);
  }

  private startTimer(ms: number): void {
    if (this.timerId !== null) clearTimeout(this.timerId);
    if (ms <= 0) {
      this.timerId = null;
      this.remaining = 0;
      this.startedAt = 0;
      return;
    }
    this.startedAt = Date.now();
    this.remaining = ms;
    this.timerId = setTimeout(() => this.dismiss(), ms);
  }
}
